import { type ResourceId } from '../general';

import { type CompetitionWishlistItem, type TeamMemberItem } from './wishlists';

export type MinimumAssetCountType = { MinimumAssetCount: { amount: number } };
export type MinimumAssetsOnSalePercentageType = { MinimumAssetsOnSalePercentage: { percent: number } };
export type MinimumUniqueCollectionOwnerPercentageType = {
    MinimumUniqueCollectionOwnerPercentage: { percent: number };
};
export type MinimumSalesCountType = { MinimumSalesCount: { amount: number; period_in_days: number } };

export type AptosMinimumAssetCountType = { AptosMinimumAssetCount: { amount: number } };
export type AptosMinimumAssetsOnSalePercentageType = { AptosMinimumAssetsOnSalePercentage: { percent: number } };
export type AptosMinimumUniqueCollectionOwnerPercentageType = {
    AptosMinimumUniqueCollectionOwnerPercentage: { percent: number };
};
export type AptosMinimumSalesCountType = { AptosMinimumSalesCount: { amount: number; period_in_days: number } };

export type PolygonMinimumAssetCountType = { PolygonMinimumAssetCount: { amount: number } };
export type PolygonMinimumAssetsOnSalePercentageType = { PolygonMinimumAssetsOnSalePercentage: { percent: number } };
export type PolygonMinimumUniqueCollectionOwnerPercentageType = {
    PolygonMinimumUniqueCollectionOwnerPercentage: { percent: number };
};
export type PolygonMinimumSalesCountType = { PolygonMinimumSalesCount: { amount: number; period_in_days: number } };

export type DynamicCriteriaType = Array<
    | AptosMinimumAssetCountType
    | AptosMinimumAssetsOnSalePercentageType
    | AptosMinimumUniqueCollectionOwnerPercentageType
    | AptosMinimumSalesCountType
    | PolygonMinimumAssetCountType
    | PolygonMinimumAssetsOnSalePercentageType
    | PolygonMinimumUniqueCollectionOwnerPercentageType
    | PolygonMinimumSalesCountType
    | MinimumAssetCountType
    | MinimumAssetsOnSalePercentageType
    | MinimumUniqueCollectionOwnerPercentageType
    | MinimumSalesCountType
>;

export interface GameCriteriaValues {
    max_value: number;
    max_assets: number;
    min_collection_count?: number;
    max_player_count?: number;
    max_user_submissions?: number;
}

export interface Competition {
    id: string;
    name: string;
    game_label: string;
    prizes?: Array<Prize>;
    team_prizes?: Array<Prize>;
    header_images?: Array<string>;
    description?: string;
    landing_page_url?: string;
    sponsor?: string;
    sponsor_logo?: string;
    max_value: number;
    max_assets: number;
    min_collection_count?: number;
    max_player_count?: number;
    max_user_submissions?: number;
    multigame?: boolean;
    published_at?: string;
    submission_starts_at?: string;
    submission_ends_at?: string;
    ends_at?: string;
    created_at?: Date;
    updated_at?: Date;
    slug: string;
    dynamic_criteria: DynamicCriteriaType;
    wishlists?: CompetitionWishlistItem[];
    wishlist_id?: string;
    rules?: string;
    questions_and_answers?: Array<CompetitionQnA>;
    winners?: Array<Winner>;
    team_winners?: Array<TeamWinner>;
    marketing_email_frequency_sec?: number;
}

export interface Prize {
    reward: string;
    name?: string;
    image_url?: string;
    link_url?: string;
    description: string;
    rank: number;
}

export interface CompetitionQnA {
    question: string;
    answers: Array<string>;
}

export type CreateCompetition = Omit<Competition, 'id' | 'created_at' | 'updated_at' | 'slug'>;

export type PatchCompetitionParams = Partial<Competition>;

export enum Period {
    Past = 'Past',
    Present = 'Present',
    Future = 'Future',
}

export enum ApplicationStatus {
    Available = 'Available',
    Pending = 'Pending',
    Submitted = 'Submitted',
}

export interface FilterCompetitionParams {
    application_status?: ApplicationStatus;
    period?: Period;
}

export enum WinnerTable {
    Teams = 'Teams',
    User = 'User',
}

export interface Winner {
    id: ResourceId;
    competition_id: Competition['id'];
    user_id: ResourceId;
    winner_id?: ResourceId;
    prize: Prize;
    reason?: string;
    winner_table?: WinnerTable;
    main_table?: WinnerTable;
    created_at?: string;
    updated_at?: string;
}

export interface TeamWinner extends Partial<Winner> {
    team_id?: string;
    team_name?: string;
    collection_samples?: string[];
    team_description?: string;
    members?: TeamMemberItem[];
}

interface LastSale {
    buyer_address?: string;
    quantity?: string;
    token_id?: string;
    marketplace?: string;
    contract?: string;
    block_timestamp?: number;
    block?: string;
    transaction_hash?: string;
    seller_address?: string;
}

interface SalesCount {
    eth_value: number;
    days_offset: number;
    sale_count: number;
}

export interface EsCollection {
    id?: string;
    name: string;
    key_image?: string;
    banner_image_url?: string;
    address?: string;
    slug?: string;
    description?: string;
    floor_price?: string;
    current_owners_count?: number;
    listed_assets_count?: number;
    player_count?: {
        count: number;
        count_1day: number;
    };
    spam_score?: number;
    total_supply?: string;
    last_sale?: LastSale;
    sale_counts?: SalesCount[];
}

export interface DiscoverCollectionResponse {
    collections: EsCollection[];
}

export type FreshCollectionResponse = {
    slugs: string[];
};

export type FreshIdeas = {
    general: {
        slug: string;
        floor_price_wei: string;
        metadata: {
            parsed: {
                name: string;
                image_url: string;
                description: string;
                external_url: string;
                banner_image_url: string;
            };
            original: {
                name: string;
                is_nsfw: boolean;
                category: string;
                image_url: string;
                description: string;
                external_url: string;
                floor_prices: {
                    value: number;
                    payment_token: {
                        symbol: 'ETH' | 'MATIC' | 'APT';
                    };
                }[];
                collection_id: string;
                top_contracts: string[];
                total_quantity: string;
                banner_image_url: string;
                distinct_nft_count: string;
                instagram_username: string;
                distinct_owner_count: number;
            };
        };
    };
    salesVolume: {
        days_offset: number;
        sale_count: number;
        eth_value: number;
    } | null;
    floorPriceChange?: {
        currentPrice: string;
        currentPriceParsed: null;
        slug: string;
        previousPrices?: {
            date: string;
            price: string;
        }[];
    };
    valid: true;
    fresh: false;
};

export type FreshIdeasResponse = FreshIdeas[];
